import { ChangeDetectionStrategy, Component, computed, effect, inject, input, signal, untracked } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco';
import { AgGridAngular } from 'ag-grid-angular';
import {
  ColDef,
  GridApi,
  GridOptions,
  GridReadyEvent,
  IServerSideDatasource,
  IServerSideGetRowsParams,
  ValueFormatterParams,
  ValueGetterParams,
} from 'ag-grid-community';
import { DateHeaderComponent } from './date-header.component';
import { MeasureHeaderComponent, MeasureHeaderParams } from './measure-header.component';
import { CesDateRangeGridFilterComponent } from '../../../../../ui/grid-filters/date-range-grid-filter.component';
import { normalizeDateRange } from '../../../../../../utils/date-range-filter.utils';
import { parseTimestampFilterFromUrl, serializeTimestampFilterForUrl } from '../../../../../../utils/timestamp-filter-url.utils';
import { ButtonModule } from 'primeng/button';
import { ProgressBarModule } from 'primeng/progressbar';
import { ToggleSwitchModule } from 'primeng/toggleswitch';
import { TableApiService } from '../../../../../../services/api/table.service';
import { TableViewBuilderService } from '../../../../../../services/table-view-builder.service';
import { extractMeasureFilter } from '../../../../../../utils/table-filter.utils';
import { DateNavigationService } from '../../../../../../services/date-navigation.service';
import { ViewPreferencesService } from '../../../../../../services/view-preferences.service';
import { FormulaInputSummary } from '../../../../../../types/formula-input-entity.interface';
import {
  CesTable,
  getColumnKey,
  MeasureInfo,
  MeasureType,
  PeriodicityMode,
  TableDataColumn,
  TableDataGridRow,
  TableDataItem,
  TableDataRow,
  TableDataValueType,
  TableMeasureRow,
  TableViewMeasure,
} from '../../../../../../types/table.interface';
import { UnitConversionQueryResponse } from '../../../../../../services/api/generated/model';
import { CesButtonSecondaryComponent } from '../../../../../ui/ces-buttons/ces-button-secondary.component';
import { CesDateNavigationComponent } from '../../../../../ui/ces-date-navigation/ces-date-navigation.component';
import { CesGridSkeletonComponent } from '../../../../../ui/skeletons/grid-skeleton.component';
import { ToastService } from '../../../../../../services/toast.service';
import { ConfirmationHelperService } from '../../../../../../services/confirmation-helper.service';
import { formatGridDateTime } from '../../../../../../utils/date.utils';
import { downloadBlob } from '../../../../../../utils/download.utils';
import { withRemoteChangeFlash } from '../../../../../../utils/grid/remote-change-flash';
import {
  countSelectedCells,
  ENTER_APPLY_NUMBER_FILTER_PARAMS,
  formatCellForClipboard,
  noRowsOverlay,
  syncNoRowsOverlay,
} from '../../../../../../utils/grid/grid-presets';
import { isKeystrokeClaimedElsewhere } from '../../../../../ui/grid-clipboard/grid-clipboard.utils';
import { agGridLocale } from '../../../../../../utils/grid/grid-presets';
import { FullscreenDirective } from '../../../../../../directives/fullscreen.directive';
import { registerWsUpdateRefresh } from '../../../../../../utils/measurement-update-refresh';
import { measurementUpdateTargetsAsset } from '../../../../../../utils/websocket-match';
import { MeasurementUpdatePayload, WebSocketMessageType } from '../../../../../../types/websocket.types';

/** Coalesce a burst of calc broadcasts (one frame per measure on a bulk recalc) into one refetch. */
const LIVE_REFRESH_DEBOUNCE_MS = 800;

interface ExportProgress {
  loaded: number;
  total: number;
}

/** Marqueur interne : bloc d'export refusé par le serveur (plage trop large). */
const EXPORT_RANGE_TOO_LARGE = 'EXPORT_RANGE_TOO_LARGE';

@Component({
  selector: 'ces-table-data-tab',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [DateNavigationService],
  imports: [
    FormsModule,
    TranslocoModule,
    AgGridAngular,
    ButtonModule,
    ToggleSwitchModule,
    CesButtonSecondaryComponent,
    CesDateNavigationComponent,
    CesGridSkeletonComponent,
    FullscreenDirective,
    ProgressBarModule,
  ],
  templateUrl: './table-data-tab.component.html',
  host: {
    'class': 'flex flex-col flex-1 min-h-0',
    '(document:keydown.escape)': 'onDocumentEscape($event)',
  },
})
export class TableDataTabComponent {
  private readonly translate = inject(TranslocoService);
  readonly localeText = agGridLocale(this.translate.getActiveLang());
  private readonly tableApiService = inject(TableApiService);
  private readonly tableViewBuilder = inject(TableViewBuilderService);
  private readonly viewPrefs = inject(ViewPreferencesService);
  private readonly toastService = inject(ToastService);
  private readonly confirmationHelper = inject(ConfirmationHelperService);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  readonly dateNavService = inject(DateNavigationService);

  readonly table = input.required<CesTable>();
  readonly assetId = input.required<number>();
  readonly configDirty = input<boolean>(false);
  readonly pendingMeasures = input<TableMeasureRow[]>([]);
  readonly configVersion = input(0);
  readonly hasSavedMeasures = input(true);
  readonly exportTrigger = input(0);

  readonly isLoading = signal(true);
  // Un export SSRM enchaîne des blocs bornés en enregistrements pendant parfois
  // plus d'une minute : l'avancement reste visible tant que le fichier n'est
  // pas servi.
  readonly exportProgress = signal<ExportProgress | null>(null);
  readonly exportPercent = computed(() => {
    const progress = this.exportProgress();
    return progress && progress.total > 0 ? Math.round((progress.loaded / progress.total) * 100) : 0;
  });
  readonly exportStatusParams = computed(() => {
    const progress = this.exportProgress();
    return { loaded: (progress?.loaded ?? 0).toLocaleString('fr-CH'), total: (progress?.total ?? 0).toLocaleString('fr-CH') };
  });
  readonly loadError = signal(false);
  readonly hideEmptyRows = signal(false);
  readonly columns = signal<TableDataColumn[]>([]);
  readonly rows = signal<TableDataRow[]>([]);
  readonly measureInfoMap = signal<Map<number, MeasureInfo>>(new Map());
  readonly unitConversionsMap = signal<Map<number, UnitConversionQueryResponse[]>>(new Map());
  private readonly lastUsedPendingHash = signal<string>('');
  private readonly loadedConfigVersion = signal(0);
  private readonly hasLoadedData = signal(false);

  private readonly alwaysRefreshable = true;

  readonly hasConfigChanges = computed(() => {
    if (this.configVersion() !== this.loadedConfigVersion()) return true;
    const pending = this.pendingMeasures();
    if (pending.length > 0 && this.configDirty()) {
      const currentHash = this.computePendingHash(pending);
      if (currentHash !== this.lastUsedPendingHash()) return true;
    }
    return this.dateNavService.hasAppliedDates() && this.dateNavService.hasPendingChanges();
  });

  readonly canRefresh = computed(() => {
    if (this.isLoading()) return false;
    if (this.loadError()) return true;
    if (!this.hasLoadedData()) {
      return this.hasSavedMeasures() || (this.pendingMeasures().length > 0 && this.configDirty());
    }
    if (this.alwaysRefreshable) return true;
    return this.hasConfigChanges();
  });

  readonly showNeedsRefresh = computed(() => {
    if (this.hasLoadedData() || this.isLoading()) return false;
    return this.hasSavedMeasures() || (this.pendingMeasures().length > 0 && this.configDirty());
  });

  readonly showStaleWarning = computed(() => this.hasLoadedData() && this.hasConfigChanges());

  readonly columnDefs = computed<ColDef<TableDataGridRow>[]>(() => this.createColumnDefs());

  readonly hasActiveFilters = signal(false);
  readonly selectedCellCount = signal(0);

  // Nothing copyable while the grid is hidden behind a loading/error/needs-refresh/empty state.
  readonly copyDisabled = computed(
    () => this.isLoading() || this.loadError() || this.showNeedsRefresh() || this.columns().length === 0,
  );

  readonly copyButtonLabel = computed(() => {
    const count = this.selectedCellCount();
    return count > 0
      ? this.translate.translate('common.buttons.copySelection', { count })
      : this.translate.translate('common.buttons.copyAllValues');
  });

  readonly defaultColDef: ColDef = {
    sortable: true,
    resizable: true,
    filter: true,
    minWidth: 100,
  };

  private readonly BASE_GRID_OPTIONS: GridOptions<TableDataGridRow> = {
    theme: 'legacy',
    animateRows: false,
    suppressColumnVirtualisation: true,
    overlayNoRowsTemplate: noRowsOverlay(this.translate.translate('common.messages.noData')),
    cellSelection: { suppressMultiRanges: false },
    processCellForClipboard: formatCellForClipboard,
    onCellSelectionChanged: event => this.selectedCellCount.set(countSelectedCells(event.api)),
    onFilterChanged: () => this.updateFilterState(),
    // Keep the grid (and its filters) mounted when empty and surface the no-rows overlay
    // from the row count, so a filter matching nothing reads as a message — never a blank
    // grid (SSRM) nor a full-panel collapse (client) — matching the follow-up grids.
    onModelUpdated: event => syncNoRowsOverlay(event.api),
    // Remote-update flash paced like the campaign grid's 3 s highlight (2 s hold + 1 s fade);
    // fires on the measure value columns via enableCellChangeFlash.
    cellFlashDuration: 2000,
    cellFadeDuration: 1000,
  };

  readonly gridOptions: GridOptions<TableDataGridRow> = {
    ...this.BASE_GRID_OPTIONS,
    getRowId: params => params.data.timestamp.toISOString(),
    onGridPreDestroyed: () => {
      this.clientGridApi = null;
      this.selectedCellCount.set(0);
    },
  };

  private readonly PER_MEASURE: PeriodicityMode = 1;
  private readonly FIXED_INTERVAL: PeriodicityMode = 3;
  private readonly SSRM_BLOCK_SIZE = 100;

  readonly useSSRM = computed(() => {
    const table = this.table();
    // Les deux modes à grille de fréquence streament par blocs sans limite de
    // plage ; le mode par campagne reste client (données réelles bornées).
    if (table.periodicity !== this.FIXED_INTERVAL && table.periodicity !== this.PER_MEASURE) return false;
    return !(this.pendingMeasures().length > 0 && this.configDirty());
  });

  readonly ssrmGridOptions: GridOptions<TableDataGridRow> = {
    ...this.BASE_GRID_OPTIONS,
    rowModelType: 'serverSide',
    // SSRM never binds rowData, so AG-Grid's auto loading overlay would never hide; the component owns the loading skeleton.
    loading: false,
    cacheBlockSize: this.SSRM_BLOCK_SIZE,
    maxBlocksInCache: 10,
    // Un drag rapide de la scrollbar ne charge que le bloc d'arrivée : les
    // blocs traversés sont ignorés, le bloc visé s'affiche plus vite.
    blockLoadDebounceMillis: 50,
    serverSideInitialRowCount: 1,
    suppressMultiSort: true,
    getRowId: params => params.data.timestamp.toISOString(),
    onGridPreDestroyed: () => {
      this.ssrmGridApi = null;
      this.selectedCellCount.set(0);
    },
  };

  private clientGridApi: GridApi<TableDataGridRow> | null = null;
  private ssrmGridApi: GridApi<TableDataGridRow> | null = null;
  private ssrmRequestVersion = 0;
  private readonly ssrmTotalRowCount = signal(0);
  private readonly ssrmHasResponded = signal(false);
  // Last query the grid served: the export loop must cover the same filtered,
  // sorted, intersected slice the user sees — not the raw applied window.
  private readonly ssrmQuery = signal<{
    range: [Date, Date] | null;
    measureFilter: ReturnType<typeof extractMeasureFilter>;
    sortField: string | undefined;
    sortOrder: string | undefined;
  } | null>(null);

  private readonly ssrmDatasource: IServerSideDatasource = {
    getRows: (params: IServerSideGetRowsParams) => {
      // Applied window only: grid-initiated refetches (sort, filter, scroll) must not
      // adopt picker dates that were never confirmed through the refresh button.
      const navStart = this.dateNavService.appliedStartDate();
      const navEnd = this.dateNavService.appliedEndDate();
      if (!navStart || !navEnd) {
        params.fail();
        return;
      }

      const range = this.intersectRangeWithColumnFilter(navStart, navEnd, params.request.filterModel);
      const sortModel = params.request.sortModel;
      const sortField = sortModel?.[0]?.colId;
      const sortOrder = sortModel?.[0]?.sort;
      const measureFilter = extractMeasureFilter(params.request.filterModel);

      // A filter/sort/range change (not pagination) makes the known total stale and
      // any in-flight block obsolete: bump the version so late responses are dropped
      // and the export guard waits until the new query has actually settled.
      if (this.ssrmQueryChanged(range, measureFilter, sortField, sortOrder)) {
        this.ssrmRequestVersion++;
        this.ssrmHasResponded.set(false);
      }
      this.ssrmQuery.set({ range, measureFilter, sortField, sortOrder });

      if (!range) {
        this.ssrmTotalRowCount.set(0);
        this.ssrmHasResponded.set(true);
        params.success({ rowData: [], rowCount: 0 });
        return;
      }

      const version = this.ssrmRequestVersion;
      const startRow = params.request.startRow ?? 0;
      const endRow = params.request.endRow ?? startRow + this.SSRM_BLOCK_SIZE;

      this.tableApiService
        .getTableDataPageAsync(
          this.table().id,
          range[0],
          range[1],
          startRow,
          endRow,
          true,
          undefined,
          this.hideEmptyRows(),
          sortField,
          sortOrder,
          measureFilter,
        )
        .then(response => {
          if (version !== this.ssrmRequestVersion) {
            params.fail();
            return;
          }
          this.ssrmHasResponded.set(true);
          const gridRows = this.transformPageToGridRows(response.data);
          // A negative total means the server counted it on an earlier block; reuse the known value.
          const rowCount = response.totalRowCount >= 0 ? response.totalRowCount : this.ssrmTotalRowCount();
          this.ssrmTotalRowCount.set(rowCount);
          params.success({ rowData: gridRows, rowCount });
        })
        .catch(err => {
          console.error('SSRM getRows failed:', err);
          // UAT 27.07 #27 : une plage au-delà du plafond serveur répond 400 —
          // sans ce message, la grille reste vide en silence.
          const cause = (err as Error)?.cause as { status?: number } | undefined;
          if (cause?.status === 400 && version === this.ssrmRequestVersion && this.ssrmRangeToastVersion !== version) {
            this.ssrmRangeToastVersion = version;
            this.toastService.warning('tables.data.rangeTooLarge');
          }
          params.fail();
        });
    },
  };

  // Un refresh peut échouer bloc par bloc : un seul message par requête servie.
  private ssrmRangeToastVersion = -1;

  private ssrmQueryChanged(
    range: [Date, Date] | null,
    measureFilter: ReturnType<typeof extractMeasureFilter>,
    sortField: string | undefined,
    sortOrder: string | undefined,
  ): boolean {
    const prev = this.ssrmQuery();
    if (!prev) return true;
    const rangeSame =
      (prev.range === null && range === null) ||
      (!!prev.range && !!range && prev.range[0].getTime() === range[0].getTime() && prev.range[1].getTime() === range[1].getTime());
    return (
      !rangeSame ||
      prev.sortField !== sortField ||
      prev.sortOrder !== sortOrder ||
      JSON.stringify(prev.measureFilter ?? null) !== JSON.stringify(measureFilter ?? null)
    );
  }

  private intersectRangeWithColumnFilter(navStart: Date, navEnd: Date, filterModel: unknown): [Date, Date] | null {
    const columnFilter = normalizeDateRange((filterModel as Record<string, unknown> | undefined)?.['timestamp']);
    if (!columnFilter) return [navStart, navEnd];

    // Filter bounds arrive resolved — a bare day already widened to its full extent, a typed time
    // kept. Widening them again here would round a `08:00-10:00` filter back out to its whole day.
    const [filterStart, filterEnd] = columnFilter;
    const start = filterStart && filterStart > navStart ? filterStart : navStart;
    const end = filterEnd && filterEnd < navEnd ? filterEnd : navEnd;
    return start > end ? null : [start, end];
  }

  onClientGridReady(event: GridReadyEvent<TableDataGridRow>): void {
    this.clientGridApi = event.api;
    this.applyTimestampFilterFromUrl(event.api);
  }

  onSsrmGridReady(event: GridReadyEvent<TableDataGridRow>): void {
    this.ssrmGridApi = event.api;
    this.applyTimestampFilterFromUrl(event.api);
    // Silent refreshes swap SSRM block data without any native flash; the wrapper diffs each
    // incoming row against the node it replaces and flashes the measure value columns.
    event.api.setGridOption(
      'serverSideDatasource',
      withRemoteChangeFlash<TableDataGridRow>(this.ssrmDatasource, {
        getApi: () => this.ssrmGridApi,
        getFlashFields: () => this.columns().map(col => getColumnKey(col.measureId, col.valueType, col.formulaInputId)),
        getRowKey: row => row.timestamp.toISOString(),
      }),
    );
  }

  // The date filter round-trips through the URL exactly like the PrimeNG grids
  // (`filters=timestamp:dd.mm.yyyy-dd.mm.yyyy`), and survives client/SSRM swaps.
  private applyTimestampFilterFromUrl(api: GridApi<TableDataGridRow>): void {
    const range = parseTimestampFilterFromUrl(this.route.snapshot.queryParams);
    if (range) api.setFilterModel({ timestamp: range });
  }

  private syncTimestampFilterToUrl(): void {
    const model = this.activeGridApi?.getFilterModel() as Record<string, unknown> | undefined;
    void this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { filters: serializeTimestampFilterForUrl(model?.['timestamp']) },
      queryParamsHandling: 'merge',
      replaceUrl: true,
    });
  }

  private get activeGridApi(): GridApi<TableDataGridRow> | null {
    return this.useSSRM() ? this.ssrmGridApi : this.clientGridApi;
  }

  clearAllFilters(): void {
    this.activeGridApi?.setFilterModel(null);
  }

  clearCellSelection(): void {
    this.activeGridApi?.clearCellSelection();
    this.activeGridApi?.clearFocusedCell();
    this.selectedCellCount.set(0);
  }

  /** Escape meant for another layer (filter field, dialog, datepicker panel...) must not drop the selection. */
  onDocumentEscape(event: KeyboardEvent): void {
    if (!isKeystrokeClaimedElsewhere(event)) this.clearCellSelection();
  }

  onCopyClick(): void {
    const count = this.selectedCellCount();
    const api = this.activeGridApi;
    if (count > 0 && api) {
      api.copySelectedRangeToClipboard();
      this.toastService.info('common.toasts.cellsCopied', undefined, undefined, undefined, { count });
      return;
    }
    void this.copyAllValues();
  }

  private updateFilterState(): void {
    this.hasActiveFilters.set(this.activeGridApi?.isAnyFilterPresent() ?? false);
    this.syncTimestampFilterToUrl();
  }

  readonly filteredRows = computed(() => {
    const allRows = this.rows();
    if (!this.hideEmptyRows()) return allRows;
    return allRows.filter(row => {
      for (const cell of row.values.values()) {
        if (cell.value !== null) return true;
      }
      return false;
    });
  });

  readonly displayRows = computed(() =>
    this.tableViewBuilder.orderRowsForDisplay(this.table().chronologicalOrder, this.filteredRows()),
  );

  readonly gridRowData = computed<TableDataGridRow[]>(() => {
    const rows = this.displayRows();
    const cols = this.columns();
    return rows.map(row => {
      const gridRow: TableDataGridRow = {
        timestamp: row.timestamp,
        campaignId: row.campaignId,
      };
      for (const col of cols) {
        const key = getColumnKey(col.measureId, col.valueType, col.formulaInputId);
        const cell = row.values.get(key);
        gridRow[key] = cell?.value ?? null;
      }
      return gridRow;
    });
  });

  private loadedTableId: number | null = null;
  private isInitialized = false;
  private lastDateHash: string | null = null;

  // Live value freshness: refetch on any calc/edit broadcast for this asset. The status badge
  // is intentionally absent on this surface — only the displayed VALUE must stay current.
  private readonly wsRefresh = registerWsUpdateRefresh<MeasurementUpdatePayload>(WebSocketMessageType.MEASUREMENT_UPDATE, {
    targets: payload => measurementUpdateTargetsAsset(payload, this.assetId()),
    refresh: () => this.onRemoteDataUpdate(),
    debounceMs: LIVE_REFRESH_DEBOUNCE_MS,
  });
  // A refresh that lands while the operator holds a cell selection is deferred, then flushed
  // once the range clears, so a background recalc never yanks an in-progress copy.
  private pendingLiveRefresh = false;

  constructor() {
    effect(() => {
      const currentTable = this.table();
      if (currentTable && currentTable.id !== this.loadedTableId) {
        this.loadedTableId = currentTable.id;
        this.hasLoadedData.set(false);
        this.columns.set([]);
        this.rows.set([]);
        this.initializeDates();
        this.isInitialized = true;
        this.lastDateHash = this.computeDateHash();
        this.isLoading.set(false);
      }
    });

    effect(() => {
      const trigger = this.exportTrigger();
      if (trigger > 0) untracked(() => void this.onExportCsv());
    });

    effect(() => {
      const hash = this.computeDateHash();
      if (!this.isInitialized || hash === this.lastDateHash) return;
      this.lastDateHash = hash;
      this.viewPrefs.saveDateRange(this.dateNavService.startDate(), this.dateNavService.endDate());
    });

    // Flush a refresh that was deferred while the operator held a cell selection.
    effect(() => {
      if (this.selectedCellCount() === 0 && this.pendingLiveRefresh) {
        this.pendingLiveRefresh = false;
        untracked(() => this.performLiveRefresh());
      }
    });
  }

  private computeDateHash(): string {
    const start = this.dateNavService.startDate()?.getTime() ?? 0;
    const end = this.dateNavService.endDate()?.getTime() ?? 0;
    return `${start}_${end}`;
  }

  private computePendingHash(measures: TableMeasureRow[]): string {
    return measures.map(m => `${m.measureId}:${m.sortNr}:${JSON.stringify(m.config)}`).join('|');
  }

  onHideEmptyRowsChange(value: boolean): void {
    this.hideEmptyRows.set(value);
    if (this.useSSRM()) {
      this.ssrmRequestVersion++;
      this.ssrmHasResponded.set(false);
      this.ssrmGridApi?.refreshServerSide({ purge: true });
    }
  }

  // A calc/edit broadcast for this asset landed. Refresh the VALUES on the live grid without
  // tearing it down (loadData() flips isLoading and the grid lives behind @if(isLoading()),
  // which would drop sort/filter/scroll/selection). Skipped until data is loaded and while the
  // staged-config view is active (it shows unsaved measures, not live server values).
  private onRemoteDataUpdate(): void {
    if (!this.hasLoadedData() || this.isLoading() || this.loadError()) return;
    if (this.pendingMeasures().length > 0 && this.configDirty()) return;
    if (this.selectedCellCount() > 0) {
      this.pendingLiveRefresh = true;
      return;
    }
    this.performLiveRefresh();
  }

  private performLiveRefresh(): void {
    if (this.useSSRM()) {
      // Re-request blocks on the live grid; scroll/sort/filter/selection are preserved and
      // the row nodes survive (no purge) so the changed cells flash their remote-update green.
      this.ssrmRequestVersion++;
      this.ssrmHasResponded.set(false);
      this.ssrmGridApi?.refreshServerSide({ purge: false });
      return;
    }
    void this.refreshClientRowsSilently();
  }

  // Client row-model silent refresh: refetch values and swap only the `rows` signal — no
  // isLoading toggle, no column rebuild — so AG-Grid delta-updates by getRowId and preserves
  // grid state. Errors are swallowed to keep the last-known values on a background refresh.
  private async refreshClientRowsSilently(): Promise<void> {
    const startDate = this.dateNavService.appliedStartDate();
    const endDate = this.dateNavService.appliedEndDate();
    if (!startDate || !endDate) return;
    try {
      const tableView = await this.tableApiService.getTableViewAsync(this.table().id, startDate, endDate, true);
      if (!tableView.measures?.length) {
        this.rows.set([]);
        return;
      }
      const formulaInputsMap = await this.loadMeasureResources(tableView.measures);
      const { aliases: inputAliases } = this.tableViewBuilder.filterFormulaInputsByData(formulaInputsMap, tableView.data);
      const rows = this.tableViewBuilder.buildRows(tableView.data, this.measureInfoMap(), this.unitConversionsMap(), inputAliases);
      this.rows.set(rows);
    } catch {
      // Silent: a background refresh must not surface a load error over valid displayed data.
    }
  }

  onRefresh(): void {
    this.viewPrefs.saveDateRange(this.dateNavService.startDate(), this.dateNavService.endDate());
    this.dateNavService.applyDates();
    this.loadedConfigVersion.set(this.configVersion());
    this.loadError.set(false);
    this.ssrmRequestVersion++;
    this.ssrmTotalRowCount.set(0);
    this.ssrmHasResponded.set(false);
    const pending = this.pendingMeasures();
    if (pending.length > 0 && this.configDirty()) {
      this.lastUsedPendingHash.set(this.computePendingHash(pending));
    }
    void this.loadData().then(() => {
      this.hasLoadedData.set(true);
      this.startLiveResilience();
      if (this.useSSRM() && this.ssrmGridApi) {
        this.ssrmGridApi.refreshServerSide({ purge: true });
      }
    });
  }

  private resilienceStarted = false;

  // The fallback poll and reconnect catch-up start after the first load — before that there is
  // nothing to keep fresh, and an eager poll interval would hold the zone perpetually unstable.
  private startLiveResilience(): void {
    if (this.resilienceStarted) return;
    this.resilienceStarted = true;
    this.wsRefresh.startFallbackPoll();
    this.wsRefresh.startReconnectCatchup();
  }

  // Seuil d'avertissement (jamais bloquant) : au-delà, l'export reste servi
  // par la boucle de blocs mais l'utilisateur confirme d'abord — un fichier
  // de cette taille prend plusieurs secondes à assembler. Compté en
  // enregistrements (lignes × mesures) : un tableau large mais court coûte
  // autant qu'un tableau étroit et long.
  private readonly EXPORT_WARNING_RECORDS = 1_000_000;

  async onExportCsv(): Promise<void> {
    const cols = this.columns();
    if (this.exportProgress()) return;
    // Le total exact n'est connu qu'après le premier bloc : exporter avant
    // contournerait silencieusement la confirmation des gros exports.
    // Niveau warning : ToastService supprime les toasts info hors dev — le
    // guidage de ces refus doit rester visible en prod (UAT 27.07 #27).
    if (cols.length === 0 || (this.useSSRM() && !this.ssrmHasResponded())) {
      this.toastService.warning('tables.data.exportNotReady');
      return;
    }
    if (!(await this.confirmLargeExport())) return;

    this.exportProgress.set({ loaded: 0, total: this.estimateExportRows() });
    try {
      let rows: TableDataRow[];
      if (this.useSSRM()) {
        rows = await this.loadAllRowsForExport(progress => this.exportProgress.set(progress));
      } else {
        rows = this.displayRows();
      }
      if (rows.length === 0) return;

      this.downloadCsv(cols, rows);
    } catch (err) {
      if ((err as Error)?.message === EXPORT_RANGE_TOO_LARGE) {
        this.toastService.warning('tables.data.exportRangeTooLarge');
      } else {
        this.toastService.error('common.toasts.exportError');
      }
    } finally {
      this.exportProgress.set(null);
    }
  }

  // Applied window: SSRM connaît le total exact dès le premier bloc servi.
  private estimateExportRows(): number {
    return this.useSSRM() ? this.ssrmTotalRowCount() : this.displayRows().length;
  }

  private async confirmLargeExport(): Promise<boolean> {
    const count = this.estimateExportRows();
    if (count * this.exportMeasureCount() <= this.EXPORT_WARNING_RECORDS) return true;
    return this.confirmationHelper.ask('tables.data.exportLargeWarning', 'tables.data.exportLargeWarningTitle', {
      count: count.toLocaleString('fr-CH'),
    });
  }

  private exportMeasureCount(): number {
    return Math.max(1, this.measureInfoMap().size);
  }

  private buildExportHeaders(cols: TableDataColumn[]): string[] {
    const headers = [this.translate.translate('tables.data.columns.date')];
    for (const col of cols) {
      const paramPart = col.parameterName ? ` ${col.parameterName}` : '';
      headers.push(`${col.measureName} (${this.getValueTypeLabel(col.valueType)}${paramPart}) [${col.unitSymbol}]`);
    }
    return headers;
  }

  private buildExportLines(cols: TableDataColumn[], rows: TableDataRow[]): string[][] {
    return rows.map(row => {
      const cells = [formatGridDateTime(row.timestamp)];
      for (const col of cols) {
        const key = getColumnKey(col.measureId, col.valueType, col.formulaInputId);
        const cell = row.values.get(key);
        cells.push(cell?.value !== null && cell?.value !== undefined ? this.formatValue(cell.value, col.decimals) : '');
      }
      return cells;
    });
  }

  private downloadCsv(cols: TableDataColumn[], rows: TableDataRow[]): void {
    const lines = [this.buildExportHeaders(cols), ...this.buildExportLines(cols, rows)];
    const csvContent = lines.map(cells => cells.join(';')).join('\n');
    const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8' });
    downloadBlob(blob, `${this.table().name}_${this.formatDateForFilename(new Date())}.csv`);
  }

  private async copyAllValues(): Promise<void> {
    const cols = this.columns();
    if (cols.length === 0 || (this.useSSRM() && !this.ssrmHasResponded())) {
      this.toastService.warning('tables.data.exportNotReady');
      return;
    }
    if (!(await this.confirmLargeExport())) return;
    try {
      const rows = this.useSSRM() ? await this.loadAllRowsForExport() : this.displayRows();
      if (rows.length === 0) {
        this.toastService.warning('common.toasts.noValuesToCopy');
        return;
      }
      const lines = [this.buildExportHeaders(cols), ...this.buildExportLines(cols, rows)];
      await navigator.clipboard.writeText(lines.map(cells => cells.join('\t')).join('\n'));
      this.toastService.success('common.toasts.valuesCopied', undefined, undefined, undefined, { count: rows.length });
    } catch (err) {
      if ((err as Error)?.message === EXPORT_RANGE_TOO_LARGE) {
        this.toastService.warning('tables.data.exportRangeTooLarge');
      } else {
        this.toastService.error('common.toasts.copyFailed');
      }
    }
  }

  // Un bloc d'export est plafonné par la tranche maximale du protocole backend
  // (20 000 lignes de grille) : le serveur recalcule la grille de fréquence sur
  // toute la plage à chaque requête, moins de blocs = moins de fois ce coût
  // fixe. Le one-shot `format=csv` n'existe plus.
  private readonly EXPORT_BLOCK_MAX_ROWS = 20_000;
  // Une ligne de grille porte un enregistrement par mesure : sur un tableau
  // large, la borne en lignes seule laissait passer un bloc unique de 121 Mo
  // (43 mesures × 14 638 lignes) et l'avancement restait à 0 jusqu'au fichier.
  private readonly EXPORT_BLOCK_RECORDS = 100_000;
  // Plancher : au-delà d'une centaine de mesures, le nombre de requêtes
  // pèserait plus que la taille des blocs, que la compression absorbe.
  private readonly EXPORT_BLOCK_MIN_ROWS = 1_000;
  // La préparation d'un bloc est bornée côté serveur : trois requêtes en vol
  // masquent la latence réseau sans saturer l'API.
  private readonly EXPORT_CONCURRENT_BLOCKS = 3;

  private exportBlockRows(): number {
    const rows = Math.floor(this.EXPORT_BLOCK_RECORDS / this.exportMeasureCount());
    return Math.min(this.EXPORT_BLOCK_MAX_ROWS, Math.max(this.EXPORT_BLOCK_MIN_ROWS, rows));
  }

  private async loadAllRowsForExport(onBlockLoaded?: (progress: ExportProgress) => void): Promise<TableDataRow[]> {
    // Reuse the grid's last served query so the file matches the filtered,
    // sorted, intersected slice the user sees — never the raw applied window.
    const query = this.ssrmQuery();
    if (!query?.range) return [];
    const [startDate, endDate] = query.range;
    const blockRows = this.exportBlockRows();

    const fetchBlock = async (startRow: number) => {
      try {
        const response = await this.tableApiService.getTableDataPageAsync(
          this.table().id,
          startDate,
          endDate,
          startRow,
          startRow + blockRows,
          true,
          undefined,
          this.hideEmptyRows(),
          query.sortField,
          query.sortOrder,
          query.measureFilter,
        );
        if (!response?.data) throw new Error('Export block failed');
        return response;
      } catch (err) {
        if ((err as Error)?.message === 'Export block failed') throw err;
        // Une plage au-delà du plafond serveur répond 400 : message guidé
        // (réduire la plage) plutôt que l'erreur d'export générique.
        const cause = (err as Error)?.cause as { status?: number } | undefined;
        if (cause?.status === 400) throw new Error(EXPORT_RANGE_TOO_LARGE);
        throw err;
      }
    };

    // Le premier bloc fixe le plan ; les suivants partent à plusieurs et se
    // rangent par index, l'ordre du fichier ne dépend donc pas de l'ordre
    // d'arrivée des réponses.
    const firstBlock = await fetchBlock(0);
    let totalRows = firstBlock.totalRowCount;
    let plannedBlocks = Math.ceil(totalRows / blockRows);
    let loadedRows = Math.min(blockRows, totalRows);
    onBlockLoaded?.({ loaded: loadedRows, total: totalRows });

    const extraBlocks: TableDataItem[][] = [];
    let nextBlockIndex = 1;
    const drainBlocks = async (): Promise<void> => {
      for (let index = nextBlockIndex++; index < plannedBlocks; index = nextBlockIndex++) {
        const response = await fetchBlock(index * blockRows);
        extraBlocks[index - 1] = response.data;
        // Le total peut croître pendant l'export (ingestion automatique vivante,
        // lignes vides masquées) : le plan s'étend avec le dernier total connu,
        // comme la boucle séquentielle historique, sinon le fichier serait
        // tronqué en silence.
        totalRows = Math.max(totalRows, response.totalRowCount);
        plannedBlocks = Math.max(plannedBlocks, Math.ceil(totalRows / blockRows));
        loadedRows = Math.min(loadedRows + blockRows, totalRows);
        onBlockLoaded?.({ loaded: loadedRows, total: totalRows });
      }
    };
    await Promise.all(Array.from({ length: Math.min(this.EXPORT_CONCURRENT_BLOCKS, Math.max(0, plannedBlocks - 1)) }, drainBlocks));

    const records: TableDataItem[] = [];
    // UAT 27.07 #27 : un bloc porte lignes × mesures enregistrements (244k
    // constatés en prod) — un push(...spread) dépasse la pile d'appels V8
    // au-delà de ~125k éléments et faisait échouer tout l'export.
    for (const record of firstBlock.data) records.push(record);
    for (const block of extraBlocks) for (const record of block ?? []) records.push(record);
    if (records.length === 0) return [];

    const infoMap = this.measureInfoMap();
    const unitConversions = this.unitConversionsMap();
    const rows = this.tableViewBuilder.buildRows(records, infoMap, unitConversions);
    return this.tableViewBuilder.orderRowsForDisplay(this.table().chronologicalOrder, rows);
  }

  getValueTypeLabel(type: TableDataValueType): string {
    const labels: Record<TableDataValueType, string> = {
      calculated: this.translate.translate('tables.data.valueTypes.calculated'),
      entered: this.translate.translate('tables.data.valueTypes.entered'),
      verificationMin: this.translate.translate('tables.data.valueTypes.verificationMin'),
      verificationMax: this.translate.translate('tables.data.valueTypes.verificationMax'),
    };
    return labels[type];
  }

  private initializeDates(): void {
    const { startDate, endDate } = this.viewPrefs.getDateRange();
    if (startDate && endDate) {
      this.dateNavService.setDates(startDate, endDate);
    }
  }

  private async loadData(): Promise<void> {
    this.isLoading.set(true);
    try {
      const pending = this.pendingMeasures();
      if (pending.length > 0 && this.configDirty()) {
        await this.loadDataWithPendingMeasures(pending);
      } else {
        await this.loadDataFromApi();
      }
    } catch {
      this.loadError.set(true);
    } finally {
      this.isLoading.set(false);
    }
  }

  private async loadDataFromApi(): Promise<void> {
    if (this.useSSRM()) {
      await this.loadSsrmMetadata();
      return;
    }

    const startDate = this.dateNavService.appliedStartDate();
    const endDate = this.dateNavService.appliedEndDate();
    if (!startDate || !endDate) return;

    const tableView = await this.tableApiService.getTableViewAsync(this.table().id, startDate, endDate, true);
    if (!tableView.measures?.length) {
      this.columns.set([]);
      this.rows.set([]);
      return;
    }

    const formulaInputsMap = await this.loadMeasureResources(tableView.measures);
    const { aliases: inputAliases } = this.tableViewBuilder.filterFormulaInputsByData(formulaInputsMap, tableView.data);
    this.setColumnsFromMeasures(tableView.measures, formulaInputsMap);

    const rows = this.tableViewBuilder.buildRows(tableView.data, this.measureInfoMap(), this.unitConversionsMap(), inputAliases);
    this.rows.set(rows);
  }

  private async loadSsrmMetadata(): Promise<void> {
    const startDate = this.dateNavService.appliedStartDate();
    const endDate = this.dateNavService.appliedEndDate();
    if (!startDate || !endDate) return;

    const tableView = await this.tableApiService.getTableViewMeasuresAsync(this.table().id, startDate, endDate);
    if (!tableView.measures?.length) {
      this.columns.set([]);
      this.rows.set([]);
      return;
    }

    const formulaInputsMap = await this.loadMeasureResources(tableView.measures);
    this.setColumnsFromMeasures(tableView.measures, formulaInputsMap);
    this.rows.set([]);
  }

  private async loadMeasureResources(measures: TableViewMeasure[]): Promise<Map<number, FormulaInputSummary[]>> {
    const { measureInfoMap, unitConversions, formulaInputsMap } = await this.tableViewBuilder.loadMeasureResources(measures);
    this.unitConversionsMap.set(unitConversions);
    this.measureInfoMap.set(measureInfoMap);
    return formulaInputsMap;
  }

  private setColumnsFromMeasures(measures: TableViewMeasure[], formulaInputsMap: Map<number, FormulaInputSummary[]>): void {
    const measuresForColumns = measures.map(m => ({ measureId: m.measureId, sortNr: m.sortNr }));
    const columns = this.tableViewBuilder.buildColumnsFromMeasures(measuresForColumns, this.measureInfoMap(), formulaInputsMap);
    this.columns.set(columns);
  }

  private transformPageToGridRows(data: TableDataItem[]): TableDataGridRow[] {
    const cols = this.columns();
    const infoMap = this.measureInfoMap();
    const unitConversions = this.unitConversionsMap();
    const rows = this.tableViewBuilder.buildRows(data, infoMap, unitConversions, new Map(), true);
    return rows.map(row => {
      const gridRow: TableDataGridRow = { timestamp: row.timestamp, campaignId: row.campaignId };
      for (const col of cols) {
        const key = getColumnKey(col.measureId, col.valueType, col.formulaInputId);
        const cell = row.values.get(key);
        gridRow[key] = cell?.value ?? null;
      }
      return gridRow;
    });
  }

  private async loadDataWithPendingMeasures(pendingMeasures: TableMeasureRow[]): Promise<void> {
    if (!pendingMeasures.length) {
      this.columns.set([]);
      this.rows.set([]);
      return;
    }
    const unitIds = this.tableViewBuilder.extractUnitIdsFromPending(pendingMeasures);
    // Includes automatic measures: their acquisition input carries the entered-column unit.
    const enteredValueMeasureIds = pendingMeasures
      .filter(m => m.config.showEnteredValue && m.measureType !== MeasureType.ANALYSIS_POST)
      .map(m => m.measureId);

    const measureIds = pendingMeasures.map(m => m.measureId);
    const [unitConversions, tableData, formulaInputsMap] = await Promise.all([
      this.tableViewBuilder.loadUnitConversionsByIds(unitIds),
      this.loadTableData(measureIds),
      this.tableViewBuilder.loadFormulaInputsForMeasureIds(enteredValueMeasureIds),
    ]);

    this.unitConversionsMap.set(unitConversions);
    const { aliases: inputAliases } = this.tableViewBuilder.filterFormulaInputsByData(formulaInputsMap, tableData);
    const measureInfoMap = this.tableViewBuilder.buildMeasureInfoMapFromPending(pendingMeasures, unitConversions);
    this.measureInfoMap.set(measureInfoMap);

    const columns = this.tableViewBuilder.buildColumnsFromMeasures(pendingMeasures, measureInfoMap, formulaInputsMap);
    this.columns.set(columns);

    const rows = this.tableViewBuilder.buildRows(tableData, measureInfoMap, unitConversions, inputAliases);
    this.rows.set(rows);
  }

  private async loadTableData(measureIds?: number[]): Promise<TableDataItem[]> {
    const startDate = this.dateNavService.appliedStartDate();
    const endDate = this.dateNavService.appliedEndDate();
    if (!startDate || !endDate) return [];
    return this.tableApiService.getTableDataAsync(this.table().id, startDate, endDate, true, measureIds);
  }

  private formatValue(value: number, decimals: number): string {
    return value.toFixed(decimals);
  }

  private formatDateForFilename(date: Date): string {
    return date.toISOString().split('T')[0] ?? '';
  }

  private createColumnDefs(): ColDef<TableDataGridRow>[] {
    const cols = this.columns();
    const dateCol: ColDef<TableDataGridRow> = {
      field: 'timestamp',
      headerName: this.translate.translate('tables.data.columns.date'),
      headerComponent: DateHeaderComponent,
      pinned: 'left',
      lockPinned: true,
      suppressMovable: true,
      minWidth: 140,
      width: 160,
      filter: CesDateRangeGridFilterComponent,
      filterValueGetter: (params: ValueGetterParams<TableDataGridRow>) => params.data?.timestamp ?? null,
      sortable: true,
      valueFormatter: (params: ValueFormatterParams<TableDataGridRow, Date>) => formatGridDateTime(params.value),
    };

    const measureCols: ColDef<TableDataGridRow>[] = cols.map(col => {
      const key = getColumnKey(col.measureId, col.valueType, col.formulaInputId);
      return {
        field: key,
        headerComponent: MeasureHeaderComponent,
        headerComponentParams: {
          measureName: col.measureName,
          valueType: col.valueType,
          unitSymbol: col.unitSymbol,
          valueTypeLabel: this.getValueTypeBadgeLabel(col.valueType),
          parameterName: col.parameterName,
        } as MeasureHeaderParams,
        headerTooltip: `${col.measureName} (${this.getValueTypeLabel(col.valueType)})${col.parameterName ? ` ${col.parameterName}` : ''}${col.unitSymbol ? ` [${col.unitSymbol}]` : ''}`,
        minWidth: 160,
        filter: 'agNumberColumnFilter',
        filterParams: ENTER_APPLY_NUMBER_FILTER_PARAMS,
        filterValueGetter: (params: ValueGetterParams<TableDataGridRow>) => {
          const val = params.data?.[key];
          if (val === null || val === undefined) return null;
          return parseFloat((val as number).toFixed(col.decimals));
        },
        cellClass: 'text-right',
        // A live refresh keeps the row nodes (getRowId); flash the values a recalc moved.
        enableCellChangeFlash: true,
        valueFormatter: (params: ValueFormatterParams<TableDataGridRow>) => {
          const val: unknown = params.value;
          if (val === null || val === undefined) return '-';
          return this.formatValue(val as number, col.decimals);
        },
      };
    });

    return [dateCol, ...measureCols];
  }

  private getValueTypeBadgeLabel(type: TableDataValueType): string {
    return this.translate.translate(`tables.data.badges.${type}`);
  }
}
